import React, { useState, useEffect, useCallback, memo } from 'react';
import { API_ENDPOINTS } from '../../../utils/apiConfig';
import { useNotifications } from '../../../contexts/NotificationContext';

/**
 * PendingInterimsWidget - Widget "Intérimaires à qualifier" pour planificateurs
 * Affiche les intérimaires créés par les chefs d'équipe en attente de validation
 */
const PendingInterimsWidget = memo(({ token, onNavigate }) => {
  const [interims, setInterims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(null);
  const { showSuccess, showError } = useNotifications();

  const fetchInterims = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.USERS_PENDING_REVIEW, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 403) {
          // Permission refusée - widget non visible pour cet utilisateur
          setInterims([]);
          return;
        }
        throw new Error('Erreur de chargement');
      }

      const data = await response.json();
      setInterims(data.success ? (data.data || []) : []);
    } catch (err) {
      console.error('Erreur fetch pending interims:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInterims();
  }, [fetchInterims]);

  const handleValidate = async (userId, userName) => {
    if (!token || validating) return;

    setValidating(userId);
    try {
      const response = await fetch(API_ENDPOINTS.USER_VALIDATE(userId), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(`${userName} validé avec succès`);
        // Retirer de la liste locale
        setInterims(prev => prev.filter(i => i.id !== userId));
      } else {
        showError(data.error || 'Erreur lors de la validation');
      }
    } catch (err) {
      console.error('Erreur validation interim:', err);
      showError('Erreur de connexion');
    } finally {
      setValidating(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Intérimaires à qualifier</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Intérimaires à qualifier</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  // Ne pas afficher le widget si pas d'intérimaires en attente
  if (interims.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>
          Intérimaires à qualifier
          <span style={styles.count}>{interims.length}</span>
        </h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('users')}
        >
          Voir utilisateurs
        </button>
      </div>

      <div style={styles.list}>
        {interims.map((interim) => (
          <div key={interim.id} style={styles.interimItem}>
            <div style={styles.interimInfo}>
              <div style={styles.interimHeader}>
                <span style={styles.interimName}>{interim.full_name}</span>
                {interim.agency_name && (
                  <span style={styles.agency}>
                    {interim.agency_code ? `[${interim.agency_code}]` : ''} {interim.agency_name}
                  </span>
                )}
              </div>
              <div style={styles.interimMeta}>
                <span style={styles.phone}>{interim.phone || 'Pas de téléphone'}</span>
                <span style={styles.date}>Créé le {formatDate(interim.created_at)}</span>
              </div>
            </div>
            <div style={styles.actions}>
              <button
                style={{
                  ...styles.editButton,
                  opacity: validating === interim.id ? 0.6 : 1
                }}
                onClick={() => onNavigate && onNavigate('users', { editUser: interim.id })}
                disabled={validating === interim.id}
                title="Modifier les informations"
              >
                Modifier
              </button>
              <button
                style={{
                  ...styles.validateButton,
                  opacity: validating === interim.id ? 0.6 : 1
                }}
                onClick={() => handleValidate(interim.id, interim.full_name)}
                disabled={validating === interim.id}
                title="Valider cet intérimaire"
              >
                {validating === interim.id ? '...' : 'Valider'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

PendingInterimsWidget.displayName = 'PendingInterimsWidget';

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '2px solid #9c27b0'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  count: {
    backgroundColor: '#9c27b0',
    color: 'white',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '600'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#9c27b0',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  interimItem: {
    padding: '14px',
    backgroundColor: '#f3e5f5',
    borderRadius: '10px',
    border: '1px solid #ce93d8',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px'
  },
  interimInfo: {
    flex: 1,
    minWidth: '200px'
  },
  interimHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '4px'
  },
  interimName: {
    fontWeight: '600',
    fontSize: '1rem',
    color: '#1a1a2e'
  },
  agency: {
    fontSize: '0.85rem',
    color: '#7b1fa2',
    fontWeight: '500'
  },
  interimMeta: {
    display: 'flex',
    gap: '16px',
    fontSize: '0.85rem',
    color: '#6c757d'
  },
  phone: {
    fontFamily: 'monospace'
  },
  date: {
    fontStyle: 'italic'
  },
  actions: {
    display: 'flex',
    gap: '8px'
  },
  editButton: {
    padding: '8px 16px',
    backgroundColor: 'white',
    color: '#6c757d',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  validateButton: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #6f42c1, #9c27b0)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '600',
    transition: 'all 0.2s'
  }
};

export default PendingInterimsWidget;
